import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';

import { usePartnersReadMe } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import Button from '@/components/Button';
import { IconEdit } from '@/components/icon/tabler/IconEdit';
import AccountMembersCard from '@/domain/account/components/AccountMembersCard';
import AccountProfileCard from '@/domain/account/components/AccountProfileCard';
import AccountStatusCard from '@/domain/account/components/AccountStatusCard';
import { getAccountEditTarget } from '@/domain/account/utils';
import { ONBOARDING_EDIT_URL } from '@/domain/account-status/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

interface Props {
  partner: Partner;
}

/**
 * `/account` (P15): the profile users see, the status with its timeline and who has access.
 * Editing an approved account keeps it approved and the document locked; in review it goes
 * through the onboarding edit.
 */
const Account = ({ partner }: Props) => {
  const t = useTranslate('domain.account.Account');
  const navigate = useNavigate();
  const { data: account } = usePartnersReadMe();
  const editTarget = getAccountEditTarget(account);

  return (
    <Stack gap={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Typography component="h1" variant="h2">
          {t('title')}
        </Typography>
        {editTarget ? (
          <Button
            variant="outlined"
            startIcon={<IconEdit />}
            onClick={() =>
              navigate(editTarget === 'account' ? ScreenRoutes.ACCOUNT_EDIT : ONBOARDING_EDIT_URL)
            }
          >
            {t('edit')}
          </Button>
        ) : null}
      </Stack>
      <Box sx={sx.grid}>
        <AccountProfileCard partner={partner} />
        <Stack gap={2.5} minWidth={0}>
          <AccountStatusCard partner={partner} />
          {account ? <AccountMembersCard account={account} /> : null}
        </Stack>
      </Box>
    </Stack>
  );
};

export default Account;

const sx = {
  grid: {
    display: 'grid',
    gap: 2.5,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 360px' },
  },
};
